import React from 'react';
import { Search, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { usePositionOptions } from '../../hooks/usePositions';

interface EmployeesFiltersProps {
  filters: {
    searchQuery: string;
    status?: string;
    position?: string;
  };
  onFiltersChange: (filters: any) => void;
}

export const EmployeesFilters: React.FC<EmployeesFiltersProps> = ({
  filters,
  onFiltersChange
}) => {
  const { positionOptions, loading: positionsLoading } = usePositionOptions();

  const statusOptions = [
    { value: 'all', label: 'Todos los estados' },
    { value: 'active', label: 'Activos' },
    { value: 'inactive', label: 'Inactivos' },
    { value: 'suspended', label: 'Suspendidos' }
  ];


  const clearFilters = () => {
    onFiltersChange({
      searchQuery: '',
      status: 'all',
      position: 'all'
    });
  };

  const hasActiveFilters = () => {
    return (
      filters.searchQuery ||
      (filters.status && filters.status !== 'all') ||
      (filters.position && filters.position !== 'all')
    );
  };

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Búsqueda */}
        <div className="lg:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Buscar empleado..."
              value={filters.searchQuery || ''}
              onChange={(e) => onFiltersChange({ searchQuery: e.target.value })}
              className="pl-10"
            />
          </div>
        </div>

        {/* Estado */}
        <div>
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) => onFiltersChange({ status: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Cargo */}
        <div>
          <Select
            value={filters.position || 'all'}
            onValueChange={(value) => onFiltersChange({ position: value })}
            disabled={positionsLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder={positionsLoading ? "Cargando cargos..." : "Cargo"} />
            </SelectTrigger>
            <SelectContent>
              {positionOptions.map((position) => (
                <SelectItem key={position.value} value={position.value}>
                  {position.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters() && (
        <div className="flex justify-between items-center mt-4 pt-4 border-t">
          <div className="text-sm text-gray-600">
            Mostrando resultados filtrados
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-gray-600 hover:text-gray-900"
          >
            <Filter className="w-4 h-4 mr-2" />
            Limpiar filtros
          </Button>
        </div>
      )}

      {!hasActiveFilters() && (
        <div className="mt-4 pt-4 border-t">
          <div className="text-sm text-gray-600">
            Mostrando todos los empleados
          </div>
        </div>
      )}
    </div>
  );
};